import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CountrySelector } from "@/components/CountrySelector";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { PasswordInput } from "@/components/PasswordInput";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { ApiPaths, api } from "@/lib/api";
import { DEFAULT_COUNTRY } from "@/lib/countries";
import { tokenStore } from "@/lib/tokenStore";
import type { Organization, TokenPair, User } from "@/lib/types";

type FormValues = {
  name: string;
  country: string;
  admin_email: string;
  admin_password: string;
  admin_first_name: string;
  admin_last_name: string;
  referral_code: string;
};

type BootstrapResponse = {
  org: Organization;
  admin: User;
  tokens: TokenPair;
};

export function SignupForm() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);
  // Cloudflare Turnstile solution token, posted with the bootstrap request
  // and verified server-side. Empty until the widget resolves.
  const [turnstileToken, setTurnstileToken] = useState("");
  const { register, handleSubmit, formState, watch, setValue } =
    useForm<FormValues>({
      defaultValues: {
        name: "",
        country: DEFAULT_COUNTRY,
        admin_email: "",
        admin_password: "",
        admin_first_name: "",
        admin_last_name: "",
        // Partner referral — prefilled from a ?ref=<code> share link.
        referral_code: searchParams.get("ref") ?? "",
      },
    });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const res = await api.post<BootstrapResponse>(ApiPaths.orgs, {
        ...values,
        turnstile_token: turnstileToken,
      });
      tokenStore.setPair(res.data.tokens.access, res.data.tokens.refresh);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setServerError(detail ?? t("auth.signup.error"));
    }
  });

  return (
    <form onSubmit={onSubmit} className="card mx-auto mt-12 max-w-lg space-y-4" noValidate>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-nb-text">{t("auth.signup.title")}</h1>
          <p className="text-sm text-nb-text-2">{t("auth.signup.subtitle")}</p>
        </div>
        <LocaleSwitcher />
      </div>

      <div>
        <label className="label" htmlFor="name">{t("auth.signup.orgName")}</label>
        <input id="name" className="input" {...register("name", { required: true })} />
      </div>

      <div>
        <label className="label" htmlFor="country">{t("auth.signup.country")}</label>
        <input type="hidden" {...register("country")} />
        <CountrySelector
          id="country"
          value={watch("country")}
          onChange={(code) =>
            setValue("country", code, { shouldDirty: true })
          }
          aria-label={t("country.select")}
        />
      </div>

      <hr className="border-nb-border" />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="admin_first_name">{t("auth.signup.firstName")}</label>
          <input id="admin_first_name" className="input" {...register("admin_first_name")} />
        </div>
        <div>
          <label className="label" htmlFor="admin_last_name">{t("auth.signup.lastName")}</label>
          <input id="admin_last_name" className="input" {...register("admin_last_name")} />
        </div>
      </div>

      <div>
        <label className="label" htmlFor="admin_email">{t("auth.signup.email")}</label>
        <input
          id="admin_email"
          type="email"
          autoComplete="email"
          className="input"
          {...register("admin_email", { required: true })}
        />
      </div>

      <div>
        <label className="label" htmlFor="admin_password">
          {t("auth.signup.password")}
        </label>
        <PasswordInput
          id="admin_password"
          autoComplete="new-password"
          {...register("admin_password", { required: true, minLength: 12 })}
        />
      </div>

      <div>
        <label className="label" htmlFor="referral_code">
          {t("auth.signup.referral")}
        </label>
        <input
          id="referral_code"
          className="input"
          placeholder={t("auth.signup.referralPlaceholder")}
          {...register("referral_code")}
        />
      </div>

      <TurnstileWidget
        onVerify={setTurnstileToken}
        className="mt-1"
        language={i18n.language?.slice(0, 2)}
      />

      {serverError && (
        <p role="alert" className="text-sm text-cvp-danger">{serverError}</p>
      )}

      <button
        type="submit"
        className="btn-primary w-full"
        disabled={formState.isSubmitting}
      >
        {t("auth.signup.submit")}
      </button>
    </form>
  );
}